import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFieldArray, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { CHURN_ZERO_EVENTS } from 'utils/constants.tsx'
import { trackEvent } from 'utils/tracking'
import { setSnackBar } from 'state/ui'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalCustomActionBar from 'components/generic/modals/form-modal/FormModalCustomActionBar'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { useAppDispatch, useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import ManageDriversProperties from './ManageDriversProperties'
import {
  DriverResponseBody,
  useGetAssetsByCategoryQuery,
  useGetDriversQuery,
  useUpdateDriverMutation,
} from '../../../../gen/rtk-openapi.ts'
import { Box, Divider, Stack } from '@mui/material'

const ManageDriversBlade = ({ onFormStateChange, onHideModal }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [showCloseModal, setShowCloseModal] = useState(false)

  const methods = useForm()
  const {
    formState: { dirtyFields },
    handleSubmit,
    control,
    watch,
    reset,
  } = methods
  const isDirty = !!Object.keys(dirtyFields).length

  const { fields } = useFieldArray({
    control,
    name: 'drivers',
  })

  const { yardDetailsList } = useAppSelector(state => state.auth)

  const watchDrivers = watch('drivers')
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchDrivers[index],
    index,
  }))

  const [updateDriver, { isLoading: isLoadingDriverUpdate }] = useUpdateDriverMutation()
  const { data: trucksData, isLoading: isLoadingTrucksData } = useGetAssetsByCategoryQuery({
    assetCategory: 'TRUCK',
  })

  const { data: trailersData, isLoading: isLoadingTrailersData } = useGetAssetsByCategoryQuery({
    assetCategory: 'TRAILER',
    filter: yardDetailsList ? JSON.stringify({ locationId: yardDetailsList[0].locationId }) : undefined,
  })

  const { data: driversData, isLoading: isLoadingDriversData } = useGetDriversQuery({})

  const isLoading = isLoadingDriversData || isLoadingTrucksData || isLoadingDriverUpdate || isLoadingTrailersData

  useGlobalLoader(isLoading)

  const formatDriver = (data, isSubmit = false) => {
    const blank = isSubmit ? null : ''
    return {
      id: data?.id,
      name: data?.name || blank,
      isActive: data?.isActive || false,
      truckId: data?.truckId || blank,
      trailerId: data?.trailerId || blank,
      gsUserId: data?.gsUserId || blank,
    }
  }

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  useEffect(() => {
    if (driversData) {
      reset({ drivers: driversData?.Items })
    }
  }, [driversData])

  const eventName = CHURN_ZERO_EVENTS.UPDATE_DRIVER
  const handleSuccess = () => {
    trackEvent(eventName)
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: 'Drivers successfully updated',
      }),
    )
    onHideModal()
    navigate(`/app/load-board/active-tickets`)
  }
  const handleError = error => {
    const errorMsg = error?.data?.error || 'There was an error updating the driver'
    trackEvent({ eventName, errorMsg })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }

  const onSubmit = async data => {
    const promises: Promise<DriverResponseBody>[] = []
    const gridResData = data.drivers

    gridResData
      .filter(driver => driver.edited)
      .forEach(driver => {
        promises.push(updateDriver({ updateDriverBody: formatDriver(driver, true), driverId: driver?.id }).unwrap())
      })

    try {
      await Promise.all(promises)
      handleSuccess()
    } catch (err) {
      handleError(err || 'An error occurred while saving')
    }
  }

  const buttons = [
    {
      id: 'submit-manage-drivers-button',
      text: 'SAVE CHANGES',
      type: 'submit',
      variant: 'contained',
      disabled: !isDirty,
    },
    {
      id: 'cancel-manage-drivers-button',
      text: 'CANCEL',
      variant: 'outlined',
      onClick: () => (isDirty ? setShowCloseModal(true) : onHideModal()),
    },
  ]

  return (
    <div>
      <AlertModal
        open={showCloseModal}
        modalType="close"
        title="Leave This Page"
        question="Are you sure you want to leave this page?"
        description="You have unsaved data on this page and all unsaved data on the ticket will be lost."
        confirmLabel="YES, LEAVE PAGE"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowCloseModal(false)
        }}
        onSubmit={() => {
          setShowCloseModal(false)
          onHideModal()
        }}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack height="100vh" sx={{ overflow: 'hidden' }}>
          <FormModalHeading title="Drivers" backButtonPosition="right" />
          <Divider />
          <Box px={4} py={2} flexGrow={1} sx={{ height: 'calc(100vh - 150px)', overflow: 'auto' }}>
            <ManageDriversProperties
              methods={methods}
              driversData={controlledFields}
              trucksData={trucksData?.Items}
              trailersData={trailersData?.Items}
              isLoading={isLoading}
            />
          </Box>
          <FormModalCustomActionBar buttonConfigs={buttons} justify="flex-start" />
        </Stack>
      </form>
    </div>
  )
}

ManageDriversBlade.propTypes = {
  onHideModal: PropTypes.func,
  onFormStateChange: PropTypes.func,
}

ManageDriversBlade.defaultProps = {
  onHideModal: null,
  onFormStateChange: null,
}

export default ManageDriversBlade
